import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Hospital as HospitalIcon, Save, ShieldCheck } from "lucide-react";

const CSV = (arr) => (arr || []).join(", ");
const toArr = (str) => (str || "").split(",").map((s) => s.trim()).filter(Boolean);

export default function AdminHospitals() {
  const [hospitals, setHospitals] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const { data } = await api.get("/hospitals");
    setHospitals(data);
    if (data.length && !selectedId) setSelectedId(data[0].id);
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const h = hospitals.find((x) => x.id === selectedId);
    if (!h) return;
    setForm({
      name: h.name || "",
      address: h.address || "",
      phone: h.phone || "",
      rating: h.rating || 4.5,
      beds_general: h.beds?.general || 0,
      beds_icu: h.beds?.icu || 0,
      beds_nicu: h.beds?.nicu || 0,
      beds_ventilator: h.beds?.ventilator || 0,
      charge_general: h.bed_charges?.general || 0,
      charge_icu: h.bed_charges?.icu || 0,
      charge_nicu: h.bed_charges?.nicu || 0,
      charge_ventilator: h.bed_charges?.ventilator || 0,
      empanelments: CSV(h.empanelments),
      in_house_facilities: CSV(h.in_house_facilities),
      specialities: CSV(h.specialities),
      departments: CSV(h.departments),
      doctors_json: JSON.stringify(h.hospital_doctors || [], null, 2),
      reg_no: h.registration?.registration_no || "",
      license_no: h.registration?.license_no || "",
      established: h.registration?.established_year || "",
      accreditation: h.registration?.accreditation || "",
      governing: h.registration?.governing_body || "",
    });
  }, [selectedId, hospitals]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      let doctors;
      try { doctors = JSON.parse(form.doctors_json || "[]"); }
      catch { toast.error("Doctors JSON is invalid"); setSaving(false); return; }
      const body = {
        name: form.name,
        address: form.address,
        phone: form.phone,
        rating: Number(form.rating),
        beds: {
          general: Number(form.beds_general || 0),
          icu: Number(form.beds_icu || 0),
          nicu: Number(form.beds_nicu || 0),
          ventilator: Number(form.beds_ventilator || 0),
        },
        bed_charges: {
          general: Number(form.charge_general || 0),
          icu: Number(form.charge_icu || 0),
          nicu: Number(form.charge_nicu || 0),
          ventilator: Number(form.charge_ventilator || 0),
        },
        empanelments: toArr(form.empanelments),
        in_house_facilities: toArr(form.in_house_facilities),
        specialities: toArr(form.specialities),
        departments: toArr(form.departments),
        hospital_doctors: doctors,
        registration: {
          registration_no: form.reg_no,
          license_no: form.license_no,
          established_year: Number(form.established) || form.established,
          accreditation: form.accreditation,
          governing_body: form.governing,
        },
      };
      await api.patch(`/admin/hospitals/${selectedId}`, body);
      toast.success("Hospital updated");
      loadAll();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-red-500/15 text-red-700 flex items-center justify-center">
          <HospitalIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Admin · Hospitals</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Manage hospital details</h1>
          <p className="text-slate-600 text-sm">Only the admin can edit empanelment, facilities, doctors, and registration.</p>
        </div>
      </div>

      <Card className="p-4 rounded-2xl mb-4">
        <Label>Choose hospital</Label>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger data-testid="admin-hosp-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {hospitals.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 rounded-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Basics & beds</div>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="f-name" value={form.name || ""} onChange={set("name")} /></div>
            <div><Label>Address</Label><Input data-testid="f-address" value={form.address || ""} onChange={set("address")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input data-testid="f-phone" value={form.phone || ""} onChange={set("phone")} /></div>
              <div><Label>Rating</Label><Input data-testid="f-rating" type="number" step="0.1" value={form.rating || ""} onChange={set("rating")} /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><Label>General</Label><Input data-testid="f-general" type="number" value={form.beds_general || 0} onChange={set("beds_general")} /></div>
              <div><Label>ICU</Label><Input data-testid="f-icu" type="number" value={form.beds_icu || 0} onChange={set("beds_icu")} /></div>
              <div><Label>NICU</Label><Input data-testid="f-nicu" type="number" value={form.beds_nicu || 0} onChange={set("beds_nicu")} /></div>
              <div><Label>Ventilator</Label><Input data-testid="f-vent" type="number" value={form.beds_ventilator || 0} onChange={set("beds_ventilator")} /></div>
            </div>
            <div>
              <Label>Bed charges per day (₹)</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                <div><Label className="text-[10px] text-slate-500">General ₹</Label><Input data-testid="f-charge-general" type="number" value={form.charge_general || 0} onChange={set("charge_general")} /></div>
                <div><Label className="text-[10px] text-slate-500">ICU ₹</Label><Input data-testid="f-charge-icu" type="number" value={form.charge_icu || 0} onChange={set("charge_icu")} /></div>
                <div><Label className="text-[10px] text-slate-500">NICU ₹</Label><Input data-testid="f-charge-nicu" type="number" value={form.charge_nicu || 0} onChange={set("charge_nicu")} /></div>
                <div><Label className="text-[10px] text-slate-500">Ventilator ₹</Label><Input data-testid="f-charge-vent" type="number" value={form.charge_ventilator || 0} onChange={set("charge_ventilator")} /></div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Empanelment & facilities</div>
          <div className="space-y-3">
            <div>
              <Label>Empanelments (comma separated)</Label>
              <Textarea data-testid="f-empanel" rows={2} value={form.empanelments || ""} onChange={set("empanelments")} placeholder="CGHS, Ayushman Bharat PMJAY, ECHS, Corporate TPA…" />
            </div>
            <div>
              <Label>In-house facilities</Label>
              <Textarea data-testid="f-facilities" rows={2} value={form.in_house_facilities || ""} onChange={set("in_house_facilities")} placeholder="Pharmacy, Diagnostic, Blood Bank, Dialysis…" />
            </div>
            <div>
              <Label>Speciality type</Label>
              <Textarea data-testid="f-specs" rows={1} value={form.specialities || ""} onChange={set("specialities")} placeholder="Super-specialty, Multi-specialty" />
            </div>
            <div>
              <Label>Departments</Label>
              <Textarea data-testid="f-depts" rows={2} value={form.departments || ""} onChange={set("departments")} placeholder="Cardiology, Neurology, Oncology…" />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl lg:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Doctors on staff (JSON)</div>
          <p className="text-xs text-slate-500 mb-2">Each entry: {'{ name, specialization, degree, experience }'}</p>
          <Textarea data-testid="f-doctors" rows={7} className="font-mono text-xs" value={form.doctors_json || "[]"} onChange={set("doctors_json")} />
        </Card>

        <Card className="p-5 rounded-2xl lg:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Registration & accreditation</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Registration no.</Label><Input data-testid="f-regno" value={form.reg_no || ""} onChange={set("reg_no")} /></div>
            <div><Label>License no.</Label><Input data-testid="f-license" value={form.license_no || ""} onChange={set("license_no")} /></div>
            <div><Label>Established year</Label><Input data-testid="f-year" type="number" value={form.established || ""} onChange={set("established")} /></div>
            <div><Label>Accreditation</Label><Input data-testid="f-accr" value={form.accreditation || ""} onChange={set("accreditation")} placeholder="NABH, NABL, JCI…" /></div>
            <div className="sm:col-span-2"><Label>Governing body</Label><Input data-testid="f-gov" value={form.governing || ""} onChange={set("governing")} /></div>
          </div>
        </Card>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <div className="text-xs text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin-only actions</div>
        <Button data-testid="admin-save-hosp" onClick={save} disabled={saving || !selectedId} className="bg-teal-600 hover:bg-teal-500 text-white">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
